import React, { useRef, useState, useEffect } from 'react';
import Button from './Button';
import { useAccessibility } from '../context/AccessibilityContext';

const VIDEOS = ['/videos/hero-bg.mp4', '/videos/hero-bg-2.mp4'];
const HERO_FALLBACK_IMG = '/images/hero-bg-2-img.png';
const HERO_FALLBACK_WEBP = '/images/hero-bg-2-img.webp';
const PRELOAD_THRESHOLD = 0.7; // Load second video when first is 70% through

const videoBaseClass = 'absolute inset-0 w-full h-full object-cover';

const HeroSection = () => {
  const videoRef0 = useRef(null);
  const videoRef1 = useRef(null);
  const secondPreloadedRef = useRef(false);
  const [activeSlot, setActiveSlot] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const { reduceMotion } = useAccessibility();

  const useStaticImage = reduceMotion || videoFailed;
  const videoRefs = [videoRef0, videoRef1];

  // Load first video only; defer until after initial paint (preload link in index.html starts fetch earlier)
  useEffect(() => {
    if (useStaticImage) return;
    const init = () => {
      const v0 = videoRef0.current;
      const v1 = videoRef1.current;
      if (v0 && v1) {
        v0.src = VIDEOS[0];
        v0.load();
        v0.play().catch(() => {});
      }
    };
    const id = typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback(init, { timeout: 100 })
      : requestAnimationFrame(init);
    return () => (typeof cancelIdleCallback !== 'undefined' ? cancelIdleCallback(id) : cancelAnimationFrame(id));
  }, [useStaticImage]);

  // Lazy-load second video when first is ~70% through
  useEffect(() => {
    if (useStaticImage) return;
    const v0 = videoRef0.current;
    const v1 = videoRef1.current;
    if (!v0 || !v1) return;
    const onTimeUpdate = () => {
      if (secondPreloadedRef.current) return;
      const dur = v0.duration;
      if (!dur || !isFinite(dur)) return;
      if (v0.currentTime / dur >= PRELOAD_THRESHOLD) {
        secondPreloadedRef.current = true;
        v1.src = VIDEOS[1];
        v1.load();
        v0.removeEventListener('timeupdate', onTimeUpdate);
      }
    };
    v0.addEventListener('timeupdate', onTimeUpdate);
    return () => v0.removeEventListener('timeupdate', onTimeUpdate);
  }, [useStaticImage]);

  const createHandleEnded = (slotThatEnded) => () => {
    const nextSlot = 1 - slotThatEnded;
    const nextVideo = videoRefs[nextSlot].current;
    if (nextVideo) {
      if (!secondPreloadedRef.current && nextSlot === 1) {
        secondPreloadedRef.current = true;
        nextVideo.src = VIDEOS[1];
        nextVideo.load();
      }
      nextVideo.currentTime = 0;
      nextVideo.play().catch(() => {});
      setActiveSlot(nextSlot);
    }
  };

  return (
    <section className="relative min-h-[300px] sm:min-h-[450px] md:min-h-[560px] overflow-hidden">
      {/* Video or static image background */}
      <div className="absolute inset-0 bg-black">
        {useStaticImage ? (
          <picture className="absolute inset-0 block w-full h-full">
            <source srcSet={HERO_FALLBACK_WEBP} type="image/webp" />
            <img
              src={HERO_FALLBACK_IMG}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 30%' }}
            />
          </picture>
        ) : (
          <>
            <video
              ref={videoRef0}
              muted
              playsInline
              preload="auto"
              poster={HERO_FALLBACK_IMG}
              onEnded={createHandleEnded(0)}
              onError={() => setVideoFailed(true)}
              className={videoBaseClass}
              style={{ objectPosition: 'center 30%', zIndex: activeSlot === 0 ? 1 : 0 }}
            />
            <video
              ref={videoRef1}
              muted
              playsInline
              preload="metadata"
              poster={HERO_FALLBACK_IMG}
              onEnded={createHandleEnded(1)}
              onError={() => setVideoFailed(true)}
              className={videoBaseClass}
              style={{ objectPosition: 'center 30%', zIndex: activeSlot === 1 ? 1 : 0 }}
            />
          </>
        )}
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Title and button */}
      <div className="relative z-20 h-full min-h-[300px] sm:min-h-[450px] md:min-h-[560px] flex items-center py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl w-full">
          <div className="hero-title-box max-w-xl px-5 py-6 sm:px-8 sm:py-10 md:px-10 md:py-12 rounded-lg bg-black/50 backdrop-blur-sm">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight text-left">
              Strengthening the Human Spirit
            </h1>
            <Button
              href="/our-story/ifys-story"
              variant="primary"
              size="lg"
              className="mt-6 focus:ring-white focus:ring-offset-black/50"
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>

      {/* Wavy layers overlaying bottom of hero */}
      <div className="absolute bottom-0 left-0 right-0 z-30 h-[70px] sm:h-[100px] md:h-[140px]">
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 1440 140"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M0 60 C180 0, 360 110, 540 50 C720 -10, 900 120, 1080 50 C1260 -10, 1350 100, 1440 60 L1440 140 L0 140 Z"
            fill="#0F3656"
          />
          <path
            d="M0 80 C180 25, 360 130, 540 75 C720 20, 900 130, 1080 75 C1260 20, 1350 120, 1440 80 L1440 140 L0 140 Z"
            fill="#1e4d6b"
          />
          <path
            d="M0 100 C180 55, 360 140, 540 95 C720 50, 900 140, 1080 95 C1260 50, 1350 130, 1440 100 L1440 140 L0 140 Z"
            fill="#5a9bb8"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
